import {useQuery} from "@apollo/client";
import {useRef} from "react";

import {Accordion} from "@/components/accordion/Accordion.tsx";
import {Card} from "@/components/card/Card.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {SelectTableFilter} from "@/components/selectTableFilter/SelectTableFilter.tsx";
import {Paginator} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {SortingDirection} from "@/components/table/Table.types.ts";
import {useTable} from "@/lib/hooks/useTable.ts";
import {
  CloudStatusComponentHistoriesResponse,
  CloudStatusComponentSortOrder,
  GQL_CLOUD_STATUS_CHECK_COMPONENT_HISTORIES,
} from "@/lib/queries/checks.ts";
import {ThirdPartyServiceComponent} from "@/lib/types/checks.ts";
import {CloudStatusIncidentHistory} from "@/pages/checks/analysis/overview/CloudStatusIncidentHistory.tsx";

const COMPONENTS_PAGE_SIZE = 10;

const SORT_OPTIONS: Record<CloudStatusComponentSortOrder, string> = {
  [CloudStatusComponentSortOrder.LATEST]: "Latest",
  [CloudStatusComponentSortOrder.OLDEST]: "Oldest",
  [CloudStatusComponentSortOrder.A_Z]: "A-Z",
  [CloudStatusComponentSortOrder.Z_A]: "Z-A",
};

const SORT_TO_INCIDENT_DIRECTION: Record<CloudStatusComponentSortOrder, SortingDirection> = {
  [CloudStatusComponentSortOrder.LATEST]: SortingDirection.Desc,
  [CloudStatusComponentSortOrder.OLDEST]: SortingDirection.Asc,
  [CloudStatusComponentSortOrder.A_Z]: SortingDirection.Desc,
  [CloudStatusComponentSortOrder.Z_A]: SortingDirection.Desc,
};

interface CloudStatusV2IncidentHistoryProps {
  checkId: number;
  start: Date;
  end: Date;
}

export const CloudStatusV2IncidentHistory = ({checkId, start, end}: CloudStatusV2IncidentHistoryProps) => {
  const prevSortRef = useRef<CloudStatusComponentSortOrder>(CloudStatusComponentSortOrder.LATEST);
  const autoSwitchedRef = useRef(false);

  const {search, searchDebounced, searchChanged, sorting, setSorting, pagination, setPagination} = useTable({
    defaultLimit: COMPONENTS_PAGE_SIZE,
    defaultSort: CloudStatusComponentSortOrder.LATEST,
    resetPaginationOnSearch: true,
    resetPaginationOnSort: true,
    onSearchChange: (value, params) => {
      const currentSortKey = Object.values(CloudStatusComponentSortOrder).includes(
        sorting.column as CloudStatusComponentSortOrder
      )
        ? (sorting.column as CloudStatusComponentSortOrder)
        : CloudStatusComponentSortOrder.LATEST;
      const currentIsAlpha =
        currentSortKey === CloudStatusComponentSortOrder.A_Z || currentSortKey === CloudStatusComponentSortOrder.Z_A;

      if (value && !currentIsAlpha) {
        prevSortRef.current = currentSortKey;
        autoSwitchedRef.current = true;
        setSorting({column: CloudStatusComponentSortOrder.A_Z, direction: SortingDirection.Asc}, params);
      } else if (!value && autoSwitchedRef.current) {
        autoSwitchedRef.current = false;
        setSorting({column: prevSortRef.current, direction: SortingDirection.Asc}, params);
      }
    },
  });

  const sortKey = Object.values(CloudStatusComponentSortOrder).includes(sorting.column as CloudStatusComponentSortOrder)
    ? (sorting.column as CloudStatusComponentSortOrder)
    : CloudStatusComponentSortOrder.LATEST;

  if (!autoSwitchedRef.current) {
    prevSortRef.current = sortKey;
  }

  const incidentDirection = SORT_TO_INCIDENT_DIRECTION[sortKey];

  const {data, loading} = useQuery<CloudStatusComponentHistoriesResponse>(GQL_CLOUD_STATUS_CHECK_COMPONENT_HISTORIES, {
    variables: {
      serviceId: checkId,
      orderBy: sortKey,
      search: searchDebounced || undefined,
      ...pagination,
    },
  });

  const total = data?.cloudStatusCheckComponentHistories?.total ?? 0;
  const components: ThirdPartyServiceComponent[] = data?.cloudStatusCheckComponentHistories?.items ?? [];

  const isLoading = loading && components.length === 0;

  return (
    <div className="d-flex flex-column gap-4">
      <Card variant="cloudStatus" className="row">
        <div className="pulseTextH1Bold mb-3">Incident History</div>
        <div className="d-flex flex-row align-items-center justify-content-between flex-wrap gap-3 mb-3">
          <SearchInput
            value={search}
            onChange={searchChanged}
            variant="tableControlBar"
            placeholder="Search components..."
          />
          <SelectTableFilter
            enumOptions={SORT_OPTIONS}
            value={sortKey}
            onChange={(value) => {
              autoSwitchedRef.current = false;
              setSorting({
                column: (value as CloudStatusComponentSortOrder) ?? CloudStatusComponentSortOrder.LATEST,
                direction: SortingDirection.Asc,
              });
            }}
            placeholder="Sort by:"
            isSearchable={false}
            isClearable={false}
          />
        </div>
        {isLoading ? (
          <LoadingContainer />
        ) : total === 0 ? (
          <div className="pulse-color-gray-700">
            {searchDebounced ? "No matching components." : "No components found."}
          </div>
        ) : total === 1 && !searchDebounced ? (
          <CloudStatusIncidentHistory
            cloudStatus={components[0]}
            start={start}
            end={end}
            direction={incidentDirection}
          />
        ) : (
          <Accordion variant="cloudStatus">
            {components.map((component) => {
              const displayTitle = component.display_title || component.title;
              const displaySubTitle = component.display_sub_title || component.sub_title;
              const label = displaySubTitle ? `${displayTitle} - ${displaySubTitle}` : displayTitle;
              return (
                <Accordion.Item key={component.name} eventKey={component.name}>
                  <Card variant="cloudStatusv2" className="p-0">
                    <Accordion.Header reverse iconInitialAngle={-90} iconActiveAngle={0}>
                      <div className="pulseTextH2Bold">{label}</div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Divider />
                      <CloudStatusIncidentHistory
                        className="mt-3"
                        noDataClassName="mx-6 my-3"
                        cloudStatus={component}
                        start={start}
                        end={end}
                        direction={incidentDirection}
                      />
                    </Accordion.Body>
                  </Card>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </Card>

      {total > COMPONENTS_PAGE_SIZE && (
        <Paginator
          className="my-2"
          hideOptions
          offset={pagination.offset}
          limit={pagination.limit}
          updatePagination={setPagination}
          total={total}
        />
      )}
    </div>
  );
};
